import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { api } from '@/api/client';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { buildCsv } from '@/utils/csv-helper';
import { useLeadUsers } from './leads.api';
import { LeadsSubNav } from './LeadsSubNav';

/**
 * Reports — one generic renderer over `GET /leads/reports?module=…`.
 *
 * Every report returns the same shape (KPI cards + a table + which columns to chart), so adding a
 * report is a server-side change only and the screen can never disagree with the numbers behind it.
 * Export writes exactly the table on screen.
 */
const MODULES = [
  { key: 'source', label: 'By Source' },
  { key: 'owner', label: 'Sale Person Performance' },
  { key: 'status', label: 'By Status' },
  { key: 'conversion', label: 'Conversion Funnel' },
  { key: 'followups', label: 'Follow-up Activity' },
  { key: 'geography', label: 'By City' },
  { key: 'volume', label: 'Biggest Opportunities' },
];

const COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];

interface Report {
  title: string;
  kpis: { label: string; value: any; hint?: string }[];
  table: Record<string, any>[];
  chart?: { xKey: string; series: string[] };
}

export function LeadsReportsPage() {
  const { data: users } = useLeadUsers();
  const [module, setModule] = useState('source');
  const [ownerId, setOwnerId] = useState<string | number | ''>('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const params = useMemo(() => {
    const p: any = { module };
    if (ownerId) p.ownerId = ownerId;
    if (fromDate) p.fromDate = fromDate;
    if (toDate) p.toDate = toDate;
    return p;
  }, [module, ownerId, fromDate, toDate]);

  const { data, isLoading } = useQuery<Report>({
    queryKey: ['lead-report', params],
    queryFn: async () => (await api.get<Report>('/leads/reports', { params })).data,
  });

  const columns = useMemo(() => (data?.table?.length ? Object.keys(data.table[0]) : []), [data]);

  const exportCsv = () => {
    if (!data?.table?.length) return;
    const csv = buildCsv(data.table, columns.map((c) => ({ key: c, label: c })));
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-${module}-report.csv`;
    document.body.appendChild(a);
    a.click();
    // Revoked on the next tick, not immediately: revoking in the same frame can beat the browser
    // to the download and the click silently does nothing.
    setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 0);
  };

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Lead Reports</h1>
          <p className="text-xs text-gray-500 mt-0.5">{data?.title ?? 'Pick a report'}</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-xs text-gray-500 block">Report</label>
            <select className="border rounded px-2 py-1.5 text-sm dark:bg-slate-900" value={module} onChange={(e) => setModule(e.target.value)}>
              {MODULES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block">Sale Person</label>
            <SearchableSelect value={ownerId} onChange={setOwnerId} allowClear placeholder="Everyone"
              options={(users ?? []).map((u) => ({ value: u.id, label: u.name }))} className="min-w-[170px]" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block">From</label>
            <input type="date" className="border rounded px-2 py-1.5 text-sm dark:bg-slate-900" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block">To</label>
            <input type="date" className="border rounded px-2 py-1.5 text-sm dark:bg-slate-900" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <button onClick={exportCsv} disabled={!data?.table?.length}
            className="px-3 py-2 border border-emerald-600 text-emerald-700 rounded text-sm inline-flex items-center gap-1 disabled:opacity-40">
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      <LeadsSubNav />

      {isLoading ? (
        <div className="text-center py-16 text-gray-400">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            {(data?.kpis ?? []).map((k, i) => (
              <div key={i} className="bg-white dark:bg-slate-900 border rounded p-3">
                <div className="text-[11px] uppercase tracking-wide text-gray-500">{k.label}</div>
                <div className="text-2xl font-semibold mt-0.5">
                  {typeof k.value === 'number' ? k.value.toLocaleString('en-IN') : k.value}
                </div>
                {k.hint && <div className="text-[11px] text-gray-400">{k.hint}</div>}
              </div>
            ))}
          </div>

          {!!data?.chart && !!data.table.length && (
            <div className="bg-white dark:bg-slate-900 border rounded p-3 mb-4">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.table.slice(0, 20)}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey={data.chart.xKey} tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  {data.chart.series.map((s, i) => <Bar key={s} dataKey={s} fill={COLORS[i % COLORS.length]} radius={[3, 3, 0, 0]} />)}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="bg-white dark:bg-slate-900 border rounded overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-emerald-50 dark:bg-slate-800 text-xs uppercase text-emerald-900 dark:text-slate-200">
                <tr>{columns.map((c) => <th key={c} className="text-left px-3 py-2 whitespace-nowrap">{c}</th>)}</tr>
              </thead>
              <tbody>
                {(data?.table ?? []).map((r, i) => (
                  <tr key={i} className="border-t">
                    {columns.map((c) => (
                      <td key={c} className="px-3 py-2 whitespace-nowrap">
                        {typeof r[c] === 'number' ? r[c].toLocaleString('en-IN') : String(r[c] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
                {!(data?.table ?? []).length && (
                  <tr><td colSpan={Math.max(1, columns.length)} className="text-center py-8 text-gray-400">Nothing in this period.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
